import React, { useState, useEffect, useCallback, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, Card, Modal, Input } from 'react-daisyui'
import { API } from '../../utils/api'
import { Api } from '../../utils/apiServer'
import DefaultVoucher from "../../assets/default-voucher.png"
import DefaultProduct from "../../assets/default-product.jpg"
import { ToastContainer, toast } from 'react-toastify'
import { AiOutlineCalendar, AiOutlineShop, AiOutlineEye, AiOutlineGift, AiOutlineTrophy, AiOutlineClockCircle, AiOutlineSafety, AiOutlineClose } from "react-icons/ai"
import { FaCoins } from "react-icons/fa"
import { formatVoucherDiscount } from '../../utils/voucherDiscount'
import { ThemeContext } from '../../context/ThemeContext'

const REDEEM_TOKEN_REQUEST_TIMEOUT = 20000

const lightenHexColor = (color, amount = 0.78) => {
    const normalized = `${color || ''}`.trim().replace('#', '')
    const hex = normalized.length === 3
        ? normalized.split('').map((character) => `${character}${character}`).join('')
        : normalized

    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return '#f6ddda'

    const channel = (offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)
    const lightened = [channel(0), channel(2), channel(4)]
        .map((value) => Math.round(value + ((255 - value) * amount)))
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('')

    return `#${lightened}`
}

function Voucher() {
    const { primaryColor } = useContext(ThemeContext)
    const [voucher, setVoucher] = useState([])
    const [page, setPage] = useState(1)
    const [lastPage, setLastPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [selectedVoucher, setSelectedVoucher] = useState(null)
    const [showDetailModal, setShowDetailModal] = useState(false)
    const [redeemVoucher, setRedeemVoucher] = useState(null)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [showTokenModal, setShowTokenModal] = useState(false)
    const [requestingToken, setRequestingToken] = useState(false)
    const [verifyingToken, setVerifyingToken] = useState(false)
    const [redeemRequest, setRedeemRequest] = useState(null)
    const [redeemToken, setRedeemToken] = useState('')
    const [tokenError, setTokenError] = useState('')
    const [tokenExpiresAt, setTokenExpiresAt] = useState(null)
    const [remainingSeconds, setRemainingSeconds] = useState(0)
    const [category, setCategory] = useState('marketplace')
    const [memberPoint, setMemberPoint] = useState(0)
    const [purchaseSuccess, setPurchaseSuccess] = useState(null)
    const navigate = useNavigate()
    const voucherPrimaryColor = primaryColor || '#a34b3f'
    const voucherPrimaryLightColor = lightenHexColor(voucherPrimaryColor)
    const categoryButtonStyle = (isActive) => ({
        backgroundColor: isActive ? voucherPrimaryColor : voucherPrimaryLightColor,
        color: isActive ? '#ffffff' : voucherPrimaryColor,
    })

    const init = useCallback(async (targetPage = 1, append = false) => {
        if (append) setLoadingMore(true)
        else setLoading(true)
        const params = {
            access: 'auth',
            method: API.VOUCHER.type,
            url: API.VOUCHER.url,
            reqBody: {
                page: targetPage,
                rows: 5,
                category
            }
        }
        const response = await Api(params);
        if (response.status) {
            const nextRows = response.data.data || []
            setVoucher((current) => append ? [...current, ...nextRows] : nextRows)
            setLastPage(response.data.lastPage)
            setPage(response.data.page)
            setMemberPoint(Number(response.member_point || response.data?.member_point || 0))
        }
        setLoading(false)
        setLoadingMore(false)
    }, [category])

    useEffect(() => {
        init(1, false)
    }, [init])

    const changeCategory = (nextCategory) => {
        if (category === nextCategory) return

        setPage(1)
        setCategory(nextCategory)
    }

    useEffect(() => {
        if (!showTokenModal || !tokenExpiresAt) {
            return
        }

        const syncRemainingTime = () => {
            const seconds = Math.max(0, Math.ceil((tokenExpiresAt - Date.now()) / 1000))
            setRemainingSeconds(seconds)
            if (seconds === 0) {
                setTokenError('Token sudah kedaluwarsa. Silakan ulangi proses dari tombol Ambil.')
            }
        }

        syncRemainingTime()
        const timer = setInterval(syncRemainingTime, 1000)
        return () => clearInterval(timer)
    }, [showTokenModal, tokenExpiresAt])

    const loadMore = () => {
        if (loadingMore || page >= lastPage) return
        init(page + 1, true)
    }

    const TakeVoucher = (v) => {
        setRedeemVoucher(v)
        setTokenError('')
        setRedeemToken('')
        setShowConfirmModal(true)
    }

    const requestRedeemToken = async () => {
        if (!redeemVoucher) {
            return
        }

        setRequestingToken(true)
        const params = {
            access: 'auth',
            method: API.REDEEM_REQUEST.type,
            url: API.REDEEM_REQUEST.url,
            timeout: REDEEM_TOKEN_REQUEST_TIMEOUT,
            reqBody: {
                voucher_id: redeemVoucher.voucher_id
            }
        }

        const response = await Api(params);
        if (response.status) {
            toast.success(response.message)
            const expiresInSeconds = response?.data?.expires_in_seconds || 300
            setRedeemRequest(response.data)
            setTokenExpiresAt(Date.now() + (expiresInSeconds * 1000))
            setRemainingSeconds(expiresInSeconds)
            setRedeemToken('')
            setTokenError('')
            setShowConfirmModal(false)
            setShowTokenModal(true)
        } else {
            toast.error(response.isTimeout
                ? 'Pengiriman token membutuhkan waktu lebih lama. Tunggu beberapa detik, lalu tekan Kirim Token lagi untuk melanjutkan.'
                : response.message
            )
        }
        setRequestingToken(false)
    }

    const verifyRedeemToken = async () => {
        if (!redeemRequest) {
            return
        }

        if (!redeemToken.match(/^\d{6}$/)) {
            setTokenError('Token harus berupa 6 digit angka.')
            return
        }

        if (remainingSeconds === 0) {
            setTokenError('Token sudah kedaluwarsa. Silakan ulangi proses dari tombol Ambil.')
            return
        }

        setVerifyingToken(true)
        setTokenError('')

        const params = {
            access: 'auth',
            method: API.REDEEM_VERIFY.type,
            url: API.REDEEM_VERIFY.url,
            reqBody: {
                voucher_redeem_confirmation_id: redeemRequest.voucher_redeem_confirmation_id,
                token: redeemToken
            }
        }

        const response = await Api(params)
        if (response.status) {
            toast.success(response.message)
            setPurchaseSuccess(response.data || { voucher_category: redeemVoucher?.category, voucher_name_snapshot: redeemVoucher?.name })
            setShowTokenModal(false)
            setRedeemRequest(null)
            setRedeemVoucher(null)
            setRedeemToken('')
            init()
        } else {
            setTokenError(response.message)
            toast.error(response.message)
        }

        setVerifyingToken(false)
    }

    const showVoucher = (v) => {
        setSelectedVoucher(v)
        setShowDetailModal(true)
    }

    const handleCloseModal = () => {
        setShowDetailModal(false)
        setSelectedVoucher(null)
    }

    const handleCloseConfirmModal = () => {
        if (requestingToken) {
            return
        }
        setShowConfirmModal(false)
        setRedeemVoucher(null)
    }

    const handleCloseTokenModal = () => {
        if (verifyingToken) {
            return
        }
        setShowTokenModal(false)
        setRedeemRequest(null)
        setRedeemVoucher(null)
        setRedeemToken('')
        setTokenError('')
        setTokenExpiresAt(null)
        setRemainingSeconds(0)
    }

    const handleTokenChange = (event) => {
        setRedeemToken(event.target.value.replace(/\D/g, '').slice(0, 6))
        setTokenError('')
    }

    const formatPoint = (point) => Number(point || 0).toLocaleString('id-ID')

    const formatRemainingTime = (seconds) => {
        const minute = Math.floor(seconds / 60).toString().padStart(2, '0')
        const second = (seconds % 60).toString().padStart(2, '0')
        return `${minute}:${second}`
    }

    const getChannelText = (channels = []) => {
        if (channels.includes('whatsapp') && channels.includes('email')) {
            return 'Token 6 digit telah dikirim ke WhatsApp dan email Anda.'
        }

        if (channels.includes('email')) {
            return 'Token 6 digit telah dikirim ke email Anda.'
        }

        return 'Token 6 digit telah dikirim ke WhatsApp Anda.'
    }

    // Loading skeleton component
    const LoadingSkeleton = () => (
        <div className="animate-pulse space-y-4">
            <div className="bg-gray-200 h-8 rounded w-1/3 mx-auto"></div>
            <div className="grid grid-cols-1 gap-4">
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="bg-gray-200 h-80 rounded-lg"></div>
                ))}
            </div>
        </div>
    )

    // console.log('voucher',voucher)

    // Keep the current vouchers visible while the selected category is fetched.
    // The full-page skeleton is only needed for the initial load.
    if (loading && voucher.length === 0) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
                <div className="mx-auto max-w-sm px-5 py-6">
                    <LoadingSkeleton />
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
            <ToastContainer autoClose={1000} />
            <div className="mx-auto max-w-sm px-5 py-6">
                <div className='flex flex-col space-y-6 scrollbar-hide'>

                    {/* Header Section */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-xs text-slate-500">Tukar poinmu</p>
                                <h1 className="text-2xl font-bold text-gray-800">Voucher</h1>
                            </div>
                            <div className="rounded-full bg-amber-100 px-3 py-2 text-sm font-bold text-amber-800"><FaCoins className="mr-1 inline" />{formatPoint(memberPoint)} poin</div>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" className="flex-1 rounded-full border-none hover:brightness-95" style={categoryButtonStyle(category === 'marketplace')} onClick={() => changeCategory('marketplace')}>Marketplace</Button>
                            <Button size="sm" className="flex-1 rounded-full border-none hover:brightness-95" style={categoryButtonStyle(category === 'offline')} onClick={() => changeCategory('offline')}>Toko</Button>
                        </div>
                        <p className="text-sm text-slate-600">{category === 'marketplace' ? 'Dipakai saat checkout online. Tidak bisa ditukar di outlet.' : 'Ditukar dengan QR di outlet. Tidak bisa dipakai saat checkout online.'}</p>
                        <div className="hidden">
                        <div className="flex items-center justify-center space-x-2">
                            <AiOutlineGift className="text-2xl text-purple-600" />
                            <h1 className="text-2xl font-bold text-gray-800">Voucher Tersedia</h1>
                        </div>
                        <p className="text-gray-600 text-sm">Tukarkan poin Anda dengan voucher menarik</p>
                        </div>
                    </div>

                    {/* Voucher Grid */}
                    {voucher.length === 0 ? (
                        <Card className="bg-gradient-to-r from-gray-50 to-white border-2 border-dashed border-gray-300">
                            <Card.Body className="text-center py-12">
                                <AiOutlineGift className="text-6xl text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 font-medium text-lg">Belum ada voucher tersedia</p>
                                <p className="text-gray-400 text-sm mt-1">Cek kembali nanti untuk voucher terbaru</p>
                            </Card.Body>
                        </Card>
                    ) : (
                        <div className="grid grid-cols-1 gap-4">
                            {voucher.map((v, i) => (
                                <Card key={i} className="border-0 bg-white shadow-md transition-shadow hover:shadow-lg">
                                    <Card.Body
                                        className="gap-3 p-3"
                                        style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                                    >
                                        <img className="h-24 w-24 shrink-0 rounded-3xl bg-rose-50 object-cover" src={v?.product_image ? v.product_image : DefaultVoucher} alt={v?.name || 'Voucher'} onError={(e) => { e.target.src = DefaultVoucher }} />
                                        <div className="min-w-0 flex-1">
                                            <button type="button" className="block w-full text-left" onClick={() => showVoucher(v)}>
                                                <h3 className="line-clamp-2 text-base font-bold leading-tight text-slate-900">{v?.name}</h3>
                                                <p className="mt-1 line-clamp-2 text-xs text-slate-500">{v?.description || `${v?.duration} hari · ${category === 'marketplace' ? 'checkout online' : 'tukar di outlet'}`}</p>
                                            </button>
                                            <div className="mt-3 flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-1 text-sm font-bold text-[#b55749]"><FaCoins className="text-xs" /> {formatPoint(v?.number_point)} poin</div>
                                                <Button size="sm" className="shrink-0 rounded-full border-none bg-[#b55749] px-4 text-white hover:bg-[#934337]" onClick={() => TakeVoucher(v)}>Tukar</Button>
                                            </div>
                                        </div>
                                    </Card.Body>
                                </Card>
                            ))}
                        </div>
                    )}

                    {voucher.length > 0 && page < lastPage && (
                        <div className="pt-2 text-center">
                            <Button size="sm" className="rounded-full border border-slate-200 bg-white text-slate-700" loading={loadingMore} disabled={loadingMore} onClick={loadMore}>Tampilkan lebih banyak</Button>
                        </div>
                    )}
                </div>
            </div>

            <Modal.Legacy open={Boolean(purchaseSuccess)} onClickBackdrop={() => setPurchaseSuccess(null)}>
                <Modal.Body className="space-y-4 text-center">
                    <AiOutlineGift className="mx-auto text-5xl text-green-600" />
                    <h2 className="text-lg font-bold text-slate-900">Selamat, voucher berhasil dibeli</h2>
                    <p className="text-sm text-slate-600">Voucher sudah masuk ke daftar voucher Anda.</p>
                    <div className="flex gap-2"><Button className="flex-1 border-none bg-[#a34b3f] text-white" onClick={() => navigate('/redeem')}>Lihat Voucher Saya</Button><Button className="flex-1" onClick={() => setPurchaseSuccess(null)}>Lanjut pilih voucher</Button></div>
                </Modal.Body>
            </Modal.Legacy>

            {/* Voucher Detail Modal */}
            <Modal.Legacy open={showDetailModal} onClickBackdrop={handleCloseModal}>
                <Modal.Header className="bg-gradient-to-r from-purple-500 to-pink-600 text-white">
                    <div className="flex items-center space-x-2">
                        <AiOutlineEye className="text-xl" />
                        <span className="font-bold text-lg">Detail Voucher</span>
                    </div>
                </Modal.Header>

                <Modal.Body className="space-y-4 max-h-96 overflow-y-auto">
                    {selectedVoucher && (
                        <>
                            {/* Voucher Image */}
                            <div className="flex justify-center">
                                <img
                                    className='w-full max-w-sm rounded-lg shadow-md'
                                    src={selectedVoucher?.product_image ? selectedVoucher?.product_image : DefaultProduct}
                                    alt="Voucher"
                                    onError={(e) => {
                                        e.target.src = DefaultProduct
                                    }}
                                />
                            </div>

                            {/* Voucher Info */}
                            <div className="space-y-3">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <h3 className="font-bold text-lg text-gray-800">{selectedVoucher?.name}</h3>
                                    <p className="text-gray-600 text-sm">{selectedVoucher?.sku}</p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="flex items-center space-x-2">
                                        <AiOutlineCalendar className="text-blue-500" />
                                        <div>
                                            <p className="text-xs text-gray-500">Masa berlaku</p>
                                            <p className="font-semibold text-gray-800">
                                                {selectedVoucher?.duration} hari
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex items-center space-x-2">
                                        <FaCoins className="text-amber-500" />
                                        <div>
                                            <p className="text-xs text-gray-500">Biaya Point</p>
                                            <p className="font-semibold text-amber-600">
                                                {formatPoint(selectedVoucher?.number_point)} poin
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center space-x-2">
                                    <AiOutlineShop className="text-green-500" />
                                    <div>
                                        <p className="text-xs text-gray-500">Penyedia</p>
                                        <p className="font-semibold text-gray-800">{selectedVoucher?.partner?.name}</p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="font-semibold text-gray-800 mb-2">Deskripsi</h4>
                                    <p className="text-gray-600 text-sm leading-relaxed">{selectedVoucher?.description}</p>
                                </div>

                                <div className="bg-red-50 p-3 rounded-lg">
                                    <p className="text-xs text-red-500">Nilai voucher</p>
                                    <p className="font-semibold text-red-700">{formatVoucherDiscount(selectedVoucher)}</p>
                                </div>

                                {/* Redeem Locations */}
                                {selectedVoucher?.partner?.redeem_merchants && selectedVoucher.partner.redeem_merchants.length > 0 && (
                                    <div>
                                        <h4 className="font-semibold text-gray-800 mb-2">Lokasi Penukaran</h4>
                                        <div className="space-y-2">
                                            {selectedVoucher.partner.redeem_merchants.map((location, i) => (
                                                <div key={i} className="bg-gray-50 p-3 rounded-lg border-l-4 border-purple-500">
                                                    <p className="font-medium text-gray-800">{location.name}</p>
                                                    <p className="text-sm text-gray-600">{location.address}</p>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </Modal.Body>

                <Modal.Actions className="justify-end space-x-2">
                    <Button variant="outline" onClick={handleCloseModal}>
                        Tutup
                    </Button>
                    <Button
                        className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-none"
                        onClick={() => {
                            const voucher = selectedVoucher
                            handleCloseModal()
                            TakeVoucher(voucher)
                        }}
                    >
                        <AiOutlineTrophy className="mr-2" />
                        Ambil Voucher
                    </Button>
                </Modal.Actions>
            </Modal.Legacy>

            {/* Redeem Confirmation Modal */}
            <Modal.Legacy open={showConfirmModal} onClickBackdrop={handleCloseConfirmModal} className="max-w-md mx-4">
                <Modal.Header className="bg-white border-b border-gray-100">
                    <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-50 text-purple-600">
                            <AiOutlineSafety className="text-xl" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-900">Konfirmasi Penukaran Voucher</h2>
                            <p className="text-xs text-gray-500">Pastikan detail voucher sudah sesuai.</p>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body className="space-y-4">
                    <p className="text-sm leading-relaxed text-gray-600">
                        Anda akan menukar poin dengan voucher berikut. Token konfirmasi akan dikirim melalui WhatsApp
                        atau email yang tersedia di profile Anda.
                    </p>

                    {redeemVoucher && (
                        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                            <div className="flex gap-3">
                                <img
                                    className="h-16 w-16 rounded-lg object-cover"
                                    src={redeemVoucher?.product_image ? redeemVoucher.product_image : DefaultVoucher}
                                    alt="Voucher"
                                    onError={(e) => {
                                        e.target.src = DefaultVoucher
                                    }}
                                />
                                <div className="min-w-0 flex-1">
                                    <h3 className="truncate text-sm font-bold text-gray-900">{redeemVoucher?.name}</h3>
                                    <p className="mt-1 truncate text-xs text-gray-500">{redeemVoucher?.partner?.name}</p>
                                    <div className="mt-3 inline-flex items-center gap-2 rounded-md bg-amber-50 px-3 py-2 text-sm font-semibold text-amber-700">
                                        <FaCoins className="text-amber-500" />
                                        {formatPoint(redeemVoucher?.number_point)} poin
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="rounded-lg border border-blue-100 bg-blue-50 px-4 py-3 text-xs leading-relaxed text-blue-700">
                        Poin akan ditahan setelah token berhasil dikirim. Jika token tidak digunakan dalam 5 menit,
                        poin akan dikembalikan otomatis.
                    </div>
                </Modal.Body>

                <Modal.Actions className="flex w-full gap-3 border-t border-gray-100 bg-[#fffaf8] px-5 py-4">
                    <Button
                        className="h-11 flex-1 border font-semibold shadow-sm hover:bg-slate-50"
                        style={{ backgroundColor: '#ffffff', borderColor: '#cbd5e1', color: '#475569' }}
                        onClick={handleCloseConfirmModal}
                        disabled={requestingToken}
                    >
                        Batal
                    </Button>
                    <Button
                        className="h-11 flex-1 border-none bg-[#a34b3f] font-semibold text-white shadow-sm hover:bg-[#873c32]"
                        onClick={requestRedeemToken}
                        loading={requestingToken}
                        disabled={requestingToken}
                    >
                        Kirim Kode
                    </Button>
                </Modal.Actions>
            </Modal.Legacy>

            {/* Token Verification Modal */}
            <Modal.Legacy open={showTokenModal} onClickBackdrop={handleCloseTokenModal} className="mx-4 max-w-md overflow-hidden rounded-[2rem]">
                <Modal.Header className="border-0 bg-white p-0">
                    <div className="relative flex w-full justify-end px-5 pb-3 pt-4">
                        <Button aria-label="Tutup" className="absolute right-0 top-0 h-11 w-11 rounded-full border-0 bg-stone-100 p-0 text-slate-800 hover:bg-stone-200" onClick={handleCloseTokenModal} disabled={verifyingToken}>
                            <AiOutlineClose className="mx-auto text-xl" />
                        </Button>
                    </div>
                </Modal.Header>

                <Modal.Body className="space-y-5 bg-white px-2 pb-2 pt-0">
                    {redeemVoucher && (
                        <div className="flex items-center gap-3 rounded-xl bg-[#fff4d7] p-2">
                            <img className="h-16 w-16 shrink-0 rounded-xl object-cover" src={redeemVoucher?.product_image || DefaultVoucher} alt={redeemVoucher?.name || 'Voucher'} onError={(event) => { event.target.src = DefaultVoucher }} />
                            <div className="min-w-0 flex-1">
                                <p className="text-xs font-semibold text-[#a56e16]">{redeemVoucher?.category === 'offline' ? 'Voucher toko' : 'Voucher marketplace'}</p>
                                <p className="mt-1 truncate text-base font-bold text-slate-900">{redeemVoucher?.name}</p>
                            </div>
                            <div className="flex shrink-0 items-center gap-1 text-base font-bold text-[#b55749]"><FaCoins className="text-sm" />{formatPoint(redeemVoucher?.number_point)}</div>
                        </div>
                    )}

                    <div>
                        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Masukan kode OTP</h2>
                        <p className="mt-1 text-sm text-slate-500">{getChannelText(redeemRequest?.channels)}</p>
                    </div>

                    <label className="relative block cursor-text">
                        <div className="grid grid-cols-6 gap-2">
                            {Array.from({ length: 6 }, (_, index) => (
                                <div key={index} className={`flex aspect-square items-center justify-center rounded-xl border-2 text-2xl font-bold ${redeemToken[index] ? 'border-[#b55749] bg-white text-slate-900' : 'border-stone-200 bg-stone-50 text-stone-300'}`}>
                                    {redeemToken[index] || ''}
                                </div>
                            ))}
                        </div>
                        <Input value={redeemToken} onChange={handleTokenChange} inputMode="numeric" pattern="[0-9]*" maxLength={6} aria-label="Kode OTP enam digit" className="absolute inset-0 h-full w-full cursor-text opacity-0" disabled={verifyingToken || remainingSeconds === 0} />
                    </label>
                    {tokenError && <p className="-mt-2 text-xs font-medium text-red-600">{tokenError}</p>}

                    <div className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-1.5 text-slate-500"><AiOutlineClockCircle /> Berlaku <strong className="text-[#b55749]">{formatRemainingTime(remainingSeconds)}</strong></span>
                        <span className="text-xs font-semibold text-slate-500">Kode dikirim satu kali</span>
                    </div>

                    <div className="flex items-start gap-2 rounded-2xl bg-stone-50 px-3 py-3 text-xs leading-relaxed text-slate-600">
                        <AiOutlineSafety className="mt-0.5 shrink-0 text-sm text-slate-500" />
                        <span>{formatPoint(redeemVoucher?.number_point)} poin ditahan selama kode berlaku. Jika kode tidak digunakan dalam 5 menit, poin kembali otomatis.</span>
                    </div>
                </Modal.Body>

                <Modal.Actions className="bg-white px-5 pb-6 pt-0">
                    <Button
                        className="h-12 w-full rounded-full border-none bg-[#a34b3f] text-base font-bold text-white hover:bg-[#873c32] disabled:bg-stone-200 disabled:text-slate-500"
                        onClick={verifyRedeemToken}
                        loading={verifyingToken}
                        disabled={verifyingToken || remainingSeconds === 0}
                    >
                        Konfirmasi penukaran
                    </Button>
                </Modal.Actions>
            </Modal.Legacy>



        </div>
    )
}

export default Voucher
